// Conservative auto-fill for the owner-alias UI (task #26).
//
// Goal: take a raw platform nickname and suggest a cleaner canonical
// display name — but only when we're confident. Misnaming an owner is
// worse than leaving a slightly-ugly raw name, so we bias toward "leave
// as-is" unless the pattern is unambiguous.
//
// Rules:
//   "dan g" → "Dan G"             (all-lowercase → title-case)
//   "jared" → "Jared"             (single lowercase word → title-case)
//   "Killa Phil" → "Killa Phil"   (mixed case → leave as-is)
//   "ZeeKill" → "ZeeKill"         (mixed case → leave as-is)
//   "MAXPOWER69" → "MAXPOWER69"   (caps + digits → leave as-is)
//   "" / "--hidden--" / "?" → "Unknown"
//   null/undefined → "Unknown"

export function suggestCanonicalName(rawName) {
  if (rawName == null) return "Unknown";
  const trimmed = String(rawName).trim();
  if (!trimmed) return "Unknown";
  if (trimmed === "--hidden--" || trimmed === "?") return "Unknown";

  // All-lowercase letters + spaces → title-case each word. Tightened with
  // \s* on the inside so single-letter parts ("g") get capitalized too.
  if (/^[a-z]+(?:\s+[a-z]+)*$/.test(trimmed)) {
    return trimmed
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  // Anything else — leave as-is. We'd rather show the raw nickname than
  // guess wrong.
  return trimmed;
}

// Extract the unique RAW owner names from a set of seasons. Used to seed
// the alias panel's row list. Dedup on a stable platform identifier (guid
// for Yahoo, user_id for Sleeper) so the panel shows one row per actual
// owner regardless of how their nickname varied year-to-year.
//
// Walks newest-first by season since the alias step's natural "raw name"
// is whichever nickname the platform shows for the latest known season.
// Stable cross-year owner key for a Yahoo team. The manager `guid` is ideal,
// but Yahoo now redacts it to the literal "--hidden--" (the SAME value for
// every manager), which — being truthy — would collapse ALL owners into one
// identity. Fall back to a nickname-based key when the guid is the sentinel or
// absent. Nicknames are not hidden and are stable enough per owner; the
// AliasPanel lets the user merge nickname variants across seasons by hand.
const HIDDEN_GUID = "--hidden--";
export function yahooOwnerIdentity(team) {
  const guid = team?.guid;
  if (guid && guid !== HIDDEN_GUID) return guid;
  return `nickname:${team?.nickname || team?.team_key || ""}`;
}

export function extractRawOwners(platform, seasons) {
  if (!Array.isArray(seasons)) return [];
  const seen = new Map(); // stable id → raw name (first-seen wins, i.e. latest season)
  for (const season of seasons) {
    if (platform === "sleeper") {
      for (const u of season.users || []) {
        const id = u.user_id;
        if (id && !seen.has(id)) {
          seen.set(id, u.display_name || `User ${id}`);
        }
      }
    } else if (platform === "yahoo") {
      for (const t of season.teams || []) {
        const id = yahooOwnerIdentity(t);
        if (id && !seen.has(id)) {
          seen.set(id, t.nickname || t.name || "Unknown Manager");
        }
      }
    }
  }
  return Array.from(seen.values()).filter(Boolean);
}
